import React from 'react';
import { companyInfo, brands } from '../data/mockData';
import { Card, CardContent } from '../components/ui/card';
import { Globe, Award, MapPin, Paintbrush, Box, FileText, Palette, Layers, Sparkles } from 'lucide-react';
import SEO from '../components/SEO';

const About = () => {
  const iconMap = {
    Paintbrush, Box, FileText, Palette, Layers, Sparkles
  };

  return (
    <div>
      <SEO title="About Us" description="Learn about Trustar Industrial Chemicals — a global distributor of TiO2 and specialty pigments serving the Middle East, Africa, and Latin America." path="/about" />
      {/* Hero Section with Premium Background */}
      <section className="relative min-h-[500px] bg-gradient-to-br from-slate-800 via-blue-900 to-indigo-950 text-white overflow-hidden">
        {/* Premium Abstract Pattern Overlay */}
        <div className="absolute inset-0 opacity-15">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 30% 50%, rgba(255,255,255,0.2) 0%, transparent 50%),
                             radial-gradient(circle at 70% 80%, rgba(255,255,255,0.15) 0%, transparent 50%)`
          }}></div>
        </div>
        
        {/* Geometric pattern */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `linear-gradient(30deg, rgba(255,255,255,0.1) 12%, transparent 12.5%, transparent 87%, rgba(255,255,255,0.1) 87.5%)`,
          backgroundSize: '80px 140px'
        }}></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-4xl">
            <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight drop-shadow-lg">About Trustar</h1>
            <p className="text-2xl text-blue-100 font-light leading-relaxed">
              {companyInfo.tagline}
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview - Who We Are + Our Mission */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Who We Are</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {companyInfo.description}
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Built on a foundation of trust and transparency, we have established ourselves as a 
                reliable partner for businesses seeking consistent quality and dependable service. 
                Our commitment to integrity in every transaction has earned us the confidence of 
                manufacturers and customers alike across three continents.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Operating from our strategic Dubai location, we bridge the gap between world-class 
                manufacturers and industrial customers with professionalism, technical expertise, 
                and unwavering dedication to your success.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-blue-600 to-indigo-800 text-white p-12 rounded-3xl shadow-xl">
              <h3 className="text-3xl font-bold mb-6">Our Mission</h3>
              <p className="text-lg leading-relaxed text-blue-50 mb-8">
                {companyInfo.mission}
              </p>
              <div className="pt-6 border-t border-white/20">
                <p className="text-sm text-blue-200 uppercase tracking-wide mb-2">Headquarters</p>
                <p className="text-white font-medium">Dubai South Business Park, UAE</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Industries We Serve</h2>
            <p className="text-xl text-gray-600">
              Delivering excellence across diverse industrial applications
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {companyInfo.industriesServed.map((industry, index) => {
              const Icon = iconMap[industry.icon];
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all group">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Icon className="text-primary" size={32} />
                    </div>
                    <h3 className="text-xl font-bold">{industry.name}</h3>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Global Presence</h2>
            <p className="text-xl text-gray-600">
              Serving key markets across three continents
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {companyInfo.regions.map((region, index) => (
              <Card key={index} className="border-none shadow-lg">
                <CardContent className="p-10 text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-full mx-auto mb-6 flex items-center justify-center">
                    <Globe className="text-primary" size={40} />
                  </div>
                  <h3 className="text-2xl font-bold">{region}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values - Moved below Global Presence */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-600">
              Principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {companyInfo.values.map((value, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl flex items-center justify-center mb-4">
                    <Award className="text-primary" size={24} />
                  </div>
                  <h3 className="text-lg font-bold">{value}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Manufacturing Partners */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Manufacturing Partners</h2>
            <p className="text-xl text-gray-600">
              Strategic alliances with industry-leading manufacturers
            </p>
          </div>

          <div className="space-y-8">
            {brands.map((brand) => (
              <Card key={brand.id} className="border-none shadow-xl overflow-hidden">
                <div className={`h-2 bg-gradient-to-r ${brand.gradient}`}></div>
                <CardContent className="p-10">
                  <div className="grid lg:grid-cols-4 gap-8 items-start">
                    <div>
                      <h3 className="text-3xl font-bold mb-2">{brand.name}</h3>
                      <p className="text-primary font-semibold text-sm mb-2">{brand.tagline}</p>
                      <p className="text-sm text-gray-500">{brand.region}</p>
                    </div>
                    <div className="lg:col-span-3">
                      <p className="text-gray-700 leading-relaxed text-lg">
                        {brand.description}
                      </p>
                      {brand.brandNote && (
                        <p className="text-gray-600 text-sm mt-3 italic">
                          {brand.brandNote}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
